import { DatePicker, Input, useToast } from "@jhool-io/fe-components";
import { useQueryClient } from "@tanstack/react-query";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { parseISO } from "date-fns";
import { useState } from "react";
import ReactDOM from "react-dom";
import { useEditSessionTime } from "../../../../hooks/schedule.mutations";
import {
    formatDate,
    formatZonedTimeToUtc,
    handleFormatDatePickerValue,
} from "../../../../../../utils/helpers";
import moment from "../../../../../../utils/moment";

interface EditSessionTimeFormProps {
    onFormSubmit(): void;
    sessionHistoryId: string;
    sessionStartDateTime: string;
    sessionEndDateTime: string;
}

interface FormProps {
    date: string;
    start_date_time: string;
    end_date_time: string;
}

export default function EditSessionTimeForm({
    onFormSubmit,
    sessionHistoryId,
    sessionEndDateTime,
    sessionStartDateTime,
}: EditSessionTimeFormProps) {
    const [localDateOfService, setLocalDateOfService] = useState<Date | null>(
        () => parseISO(sessionStartDateTime)
    );
    const { toast } = useToast();

    const queryClient = useQueryClient();

    const editTime = useEditSessionTime(sessionHistoryId);

    const {
        handleSubmit,
        register,
        control,
        formState: { errors },
    } = useForm<FormProps>({
        resolver: yupResolver(
            yup.object({
                date: yup
                    .date()
                    .required("Date of appointment is required")
                    .test(
                        "date-range",
                        "You can only add a date for this week",
                        (value) => {
                            if (
                                !moment.isMoment(value) &&
                                !(value instanceof Date)
                            ) {
                                return false;
                            }

                            const date = moment(value);
                            const startOfWeek = moment().startOf("isoWeek");
                            const endOfWeek = moment().endOf("isoWeek");

                            return (
                                date.isSameOrAfter(startOfWeek) &&
                                date.isSameOrBefore(endOfWeek)
                            );
                        }
                    ),
                start_date_time: yup
                    .string()
                    .required("Session start time is required"),
                end_date_time: yup
                    .string()
                    .required("Session end time is required")
                    .test({
                        name: "is-greater",
                        message:
                            "Session end time should be later than the start time",
                        test: (
                            value: string | undefined,
                            context: {
                                parent: {
                                    session_start_time: string;
                                };
                            }
                        ) => {
                            const { session_start_time: sessionStartTime } =
                                context.parent;
                            if (!sessionStartTime || !value) {
                                return true;
                            }
                            const start = new Date(
                                `1970-01-01T${sessionStartTime}:00`
                            );
                            const end = new Date(`1970-01-01T${value}:00`);
                            return end > start;
                        },
                    }),
            })
        ),
        mode: "onChange",
    });

    const onSubmit = (payload: FormProps) => {
        const dataToSend = {
            start_date_time: formatZonedTimeToUtc(
                `${handleFormatDatePickerValue(payload.date)}T${
                    payload.start_date_time.split(":")[0]
                }:${payload.start_date_time.split(":")[1]}:00.000`
            ),
            end_date_time: formatZonedTimeToUtc(
                `${handleFormatDatePickerValue(payload.date)}T${
                    payload.end_date_time.split(":")[0]
                }:${payload.end_date_time.split(":")[1]}:00.000`
            ),
        };

        editTime.mutate(dataToSend, {
            onSuccess: async (res) => {
                queryClient.invalidateQueries({
                    queryKey: ["provider-upcoming-sessions"],
                });

                queryClient.invalidateQueries({
                    queryKey: ["provider-sessions"],
                });

                queryClient.invalidateQueries({
                    queryKey: ["caseload"],
                });

                toast({
                    mode: "success",
                    message: res.message || "Session time updated successfully",
                });

                onFormSubmit();
            },
            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message || "Could not session time",
                });
            },
        });
    };

    return (
        <form id="edit-session-time" onSubmit={handleSubmit(onSubmit)}>
            <div className="fg">
                <Controller
                    name="date"
                    control={control}
                    defaultValue={sessionStartDateTime}
                    render={({ field }) => (
                        <DatePicker
                            label="Date of service"
                            onChange={(date) => {
                                field.onChange(date);
                                setLocalDateOfService(date);
                            }}
                            selected={localDateOfService}
                            hasError={!!errors.date}
                            errorText={
                                errors.date?.type === "typeError"
                                    ? "Invalid date value"
                                    : errors.date?.message
                            }
                            dateFormat="MMM d, yyyy"
                            isClearable
                            popperContainer={({ children }) =>
                                ReactDOM.createPortal(children, document.body)
                            }
                            popperClassName="!z-[50]"
                            popperProps={{
                                strategy: "fixed",
                                modifiers: [
                                    {
                                        name: "zIndex",
                                        options: {
                                            zIndex: 10,
                                        },
                                    },
                                ],
                            }}
                        />
                    )}
                />
            </div>
            <div className="fg">
                <Input
                    {...register("start_date_time")}
                    label="Start time"
                    placeholder="Start time"
                    defaultValue={formatDate(
                        sessionStartDateTime,
                        false,
                        "HH:mm"
                    )}
                    hasError={!!errors.start_date_time}
                    errorText={errors.start_date_time?.message}
                    type="time"
                />
            </div>
            <div>
                <Input
                    {...register("end_date_time")}
                    label="End time"
                    placeholder="End time"
                    defaultValue={formatDate(
                        sessionEndDateTime,
                        false,
                        "HH:mm"
                    )}
                    hasError={!!errors.end_date_time}
                    errorText={errors.end_date_time?.message}
                    type="time"
                />
            </div>
        </form>
    );
}
